<template>
	<div class="flex-grow flex flex-col py-5 px-8">
		<Header :title="$t('customer').toString()">
			<p>
				{{ $t('required_data_red_stars') }}
			</p>
		</Header>

		<div v-if="!isLoading" class="main">
			<DamageAssessorCaseCustomerForm v-if="person" ref="CustomerForm" v-model="person" />

			<div v-else class="font-bold">{{ $t('customer') }} {{ $t('not_stored') }}</div>
		</div>

		<LoadingSpinner v-else />

		<Footer v-if="!isLoading" @backButtonClick="goBack" :is-back-button="true" :is-continue-button="false" :is-skippable="false" class="m-8">
			<template #right>
				<BaseButton v-if="person" type="button" @click="save" :disabled="isSaving" class="btn-blue">
					{{ isSaving ? $t('general.loading') : $t('general.save') }}
				</BaseButton>
			</template>
		</Footer>
	</div>
</template>

<script>
import Header from '@/components/damagecases/Header';
import Footer from '@/components/damagecases/Footer';
import DamageAssessorCaseCustomerForm from '@/components/damageassessorcases/forms/DamageAssessorCaseCustomerForm';
import BaseButton from '@/components/forms/buttons/BaseButton';
import LoadingSpinner from '@/components/navigation/LoadingSpinner';

import DamageAssessorCaseRepository from '@/api/repositories/DamageAssessorCaseRepository';

export default {
	name: 'DamageAssessorCaseEditPerson',

	components: {
		Header,
		Footer,
		DamageAssessorCaseCustomerForm,
		BaseButton,
		LoadingSpinner,
	},

	data() {
		return {
			isLoading: true,
			isSaving: false,
			damageAssessorCase: null,
			person: null,
		};
	},

	mounted() {
		this.$store.commit('setConfirmLeaving', this.$t('leave_page'));

		this.load();
	},

	destroyed() {
		this.$store.commit('setConfirmLeaving', false);
	},

	methods: {
		load() {
			this.isLoading = true;

			new DamageAssessorCaseRepository()
				.getDamageAssessorCaseDetail(this.$route.params.id)
				.then(response => {
					this.damageAssessorCase = response.data;

					this.person = this.damageAssessorCase.person ? this.buildEditablePerson(this.damageAssessorCase.person) : null;
				})
				.catch(error => {
					console.error('Could not load DamageAssessorCase person', error);

					this.showRequestError(error);
				})
				.finally(() => {
					this.isLoading = false;
				});
		},
		buildEditablePerson(person) {
			return {
				title: person?.title ?? null,
				gender: person?.gender ?? null,
				firstName: person?.firstName ?? null,
				lastName: person?.lastName ?? null,
				street: person?.street ?? null,
				houseNumber: person?.houseNumber ?? null,
				poBox: person?.poBox ?? null,
				phoneNumber: person?.phoneNumber ?? null,
				cellNumber: person?.cellNumber ?? null,
				email: person?.email ?? null,
				organisation: person?.organisation ?? null,

				city: {
					id: person?.city?.id ?? null,
					zipCode: person?.city?.zipCode ?? null,
					name: person?.city?.name ?? null,
				},
			};
		},
		buildPersonDto() {
			const person = this.person || {};

			return {
				title: person.title ?? null,
				gender: person.gender ?? null,
				firstName: person.firstName ?? null,
				lastName: person.lastName ?? null,
				street: person.street ?? null,
				houseNumber: person.houseNumber ?? null,
				poBox: person.poBox ?? null,

				city: person.city
					? {
							id: person.city.id ?? null,
							zipCode: person.city.zipCode ?? null,
							name: person.city.name ?? null,
					  }
					: null,

				phoneNumber: person.phoneNumber ?? null,
				cellNumber: person.cellNumber ?? null,
				email: person.email ?? null,
				organisation: person.organisation ?? null,
			};
		},
		save() {
			if (this.isSaving) {
				return;
			}

			if (!this.person) {
				this.$swal(this.$t('general.error').toString(), 'Keine Person für diesen Gutachterfall vorhanden.', 'error');
				return;
			}

			if (!this.$refs.CustomerForm) {
				this.$swal(this.$t('general.error').toString(), 'Das Personenformular konnte nicht geladen werden.', 'error');
				return;
			}

			if (!this.$refs.CustomerForm.validate()) {
				this.showValidationError();
				return;
			}

			this.isSaving = true;

			const payload = {
				person: this.buildPersonDto(),
			};

			new DamageAssessorCaseRepository()
				.updateDamageAssessorCase(this.damageAssessorCase.id, payload)
				.then(() => {
					this.$store.commit('setConfirmLeaving', false);

					return this.$swal(this.$t('general.success').toString(), this.$t('general.changes_saved').toString(), 'success');
				})
				.then(() => {
					this.goBack();
				})
				.catch(error => {
					this.showRequestError(error);
				})
				.finally(() => {
					this.isSaving = false;
				});
		},
		goBack() {
			this.$store.commit('setConfirmLeaving', false);

			this.$router.push({
				name: 'damageAssessorCaseDetail',

				params: {
					id: this.$route.params.id,
				},
			});
		},
		showValidationError() {
			this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
		},
		showRequestError(error) {
			const status = error?.response?.status;

			if (status === 403) {
				this.$swal(this.$t('general.error').toString(), 'Du hast keine Berechtigung, diesen Gutachterfall zu bearbeiten.', 'error');

				return;
			}

			if (status === 422) {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');

				return;
			}

			this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
		},
	},
};
</script>
